import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import BackIcon from '@mui/icons-material/ArrowBack';
import { useIntl } from 'react-intl';
import { useShift } from '~/hooks/api/useShift';
import moment from 'moment';
import Scrollable from '~/components/Scrollable';
import ShiftsMenu from '~/components/ShiftSelect';
import { TurnResource } from '~/generated';
import { TurnDisabledView } from '~/components/TurnDisabledView';
import TurnCancellationButton from '~/components/TurnCancellationButton';
import TurnCanceledView from '~/components/TurnCanceledView';
import { useCandidates } from '~/hooks/api/useCandidates';
import messages from './messages';
import TurnMenu, { ExpandedTurnResource } from './TurnMenu';
import DisableTurnButton from './DisableTurnButton';
import CandidateAssignment from './CandidateAssignment';
import { getRouteApi, Link } from '@tanstack/react-router';

const route = getRouteApi('/weekplan/assignment/$shiftId/$turnId');

const RouteAssignment = () => {
  const intl = useIntl();
  const params = route.useParams();
  const search = route.useSearch();
  const shiftId = parseInt(params.shiftId, 10);
  const turnId = parseInt(params.turnId, 10);
  const { data: shift, isLoading: isLoadingShift } = useShift({
    id: shiftId,
    expand: ['turns.route'],
  });
  const turn = shift?.turns?.find((t) => t.id === turnId) as
    | TurnResource
    | undefined;
  const shiftDate = moment(shift?.startDate);
  const hasVisibleAssignments =
    (turn?.turnAssignments?.filter((assignment) => assignment.isVisible)
      .length ?? 0) > 0;
  const { data } = useCandidates({
    id: turnId,
  });
  const hasAssignments =
    data?.some((candidate) => candidate.assignment) || false;

  return (
    <Scrollable
      header={
        <AppBar position="static">
          <Toolbar>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center',
                flexGrow: 1,
              }}
            >
              <Grid>
                <IconButton
                  color="inherit"
                  href=""
                  component={Link}
                  to={
                    search.from ||
                    `/weekplan/${shiftDate.isoWeekYear()}/${shiftDate.isoWeek()}/${shiftDate.weekday()}/${shiftId}`
                  }
                  aria-label={intl.formatMessage(messages.back)}
                  size="large"
                >
                  <BackIcon />
                </IconButton>
              </Grid>
              <Grid>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  {shift ? moment(shift.startDate).format('dd. L') : null}
                </Typography>
              </Grid>
              <Grid size="grow">
                {turn?.route ? (
                  <ShiftsMenu
                    year={shiftDate.year()}
                    month={shiftDate.month() + 1}
                    day={shiftDate.date()}
                    selectedShift={shiftId}
                    routeId={turn.route.id}
                  />
                ) : null}
                {turn ? (
                  <TurnMenu
                    selectedTurn={turn as ExpandedTurnResource}
                    turns={(shift?.turns as ExpandedTurnResource[]) || []}
                    shiftId={shiftId}
                  />
                ) : null}
              </Grid>
              <Grid>
                {!hasAssignments && turn?.isEnabled && !turn.isCanceled ? (
                  <DisableTurnButton turnId={turnId} shiftId={shiftId} />
                ) : null}
                {hasVisibleAssignments && turn && !turn.isCanceled ? (
                  <TurnCancellationButton turnId={turnId} shiftId={shiftId} />
                ) : null}
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      }
      isLoading={isLoadingShift}
    >
      {turn?.isCanceled ? (
        <TurnCanceledView reason={turn.cancellationReason || undefined} />
      ) : null}
      {!isLoadingShift && !turn?.isEnabled ? (
        <TurnDisabledView turnId={turnId} shiftId={shiftId} />
      ) : null}
      {!turn?.isCanceled && turn?.isEnabled ? (
        <CandidateAssignment
          turnId={turnId}
          shiftId={shiftId}
          requiredBrothers={turn.requiredBrothers ?? 0}
        />
      ) : null}
    </Scrollable>
  );
};

export default RouteAssignment;
